import type { XMPPClient } from '@xmppClient';
import type { Calls, Chat, PlainObject, StringObject } from '../../types';
import type { XMPPChatRealTimeService } from '../../types/chat';
import { ChatEvent, CallType, CallSignalingModuleIdentifier } from '../../types/constants';
import { xmppClient } from '@xmppClient';
import config from '../../config';
import Utils from '../../Utils';
import ProxyService from '../../Proxy';
import ChatHelpers from './Helpers';
import ChatUtils from './ChatUtils';
import StreamManagement from './StreamManagement';
import PrivacyList from './PrivacyList';
import MultiUserChat from './MultiUserChat';
import AbstractChatService from '../AbstractChatService';

export default class XMPPChatService extends AbstractChatService implements XMPPChatRealTimeService {
  declare onMessageErrorListener: Chat.OnMessageErrorListener;
  declare onSentMessageCallback: Chat.OnMessageSentListener;
  declare onDeliveredStatusListener: Chat.OnMessageDeliveredListener;
  declare onJoinOccupant: Chat.OnDialogJoinListener;
  declare onLeaveOccupant: Chat.OnDialogLeaveListener;
  declare onKickOccupant: Chat.OnDialogKickListener;

  public xmppClient: XMPPClient;
  public helpers: ChatHelpers;
  public privacylist: PrivacyList;
  public muc: MultiUserChat;
  public streamManagement: StreamManagement;

  public stanzasCallbacks: Chat.StanzasCallbacks = {};
  private xmppClientListeners: Chat.XMPPClientListeners = new Map<Chat.XMPPClientEvent, Chat.XMPPClientListener>();
  private connectPromise: Promise<boolean> | null = null;
  private earlyIncomingMessagesQueue: Chat.XmlElement[] = [];

  constructor(proxy: ProxyService) {
    super(proxy);
    this.helpers = new ChatHelpers();

    this.xmppClient = xmppClient({
      service: config.chatProtocol.websocket,
      credentials: (auth, mechanism) =>
        auth(
          {
            username: this.xmppClient?.options?.username,
            password: this.xmppClient?.options?.password,
          },
          mechanism
        ),
    });

    if (config.chat.reconnect?.enable) {
      this.xmppClient.reconnect.delay = config.chat.reconnect.timeInterval * 1000;
    }

    const options = {
      xmppClient: this.xmppClient,
      helpers: this.helpers,
      stanzasCallbacks: this.stanzasCallbacks,
    };

    this.muc = new MultiUserChat(options);
    this.privacylist = new PrivacyList(options);
    this.streamManagement = new StreamManagement(this);
  }

  public get connectionStatus() {
    return this.xmppClient.status;
  }

  public get currentUserId(): Chat.UserId {
    const userJID = this.xmppClient.jid?.toString() ?? '';
    const userID = this.helpers.getUserIdFromJID(userJID) ?? 0;
    return userID;
  }

  public async connect(params: Chat.ConnectionParams): Promise<boolean> {
    this.connectPromise = new Promise((resolve, reject) => {
      Utils.DLog('[Chat]', 'Connect with parameter:', params);

      if (this.isConnecting) {
        Utils.DLog('[Chat]', 'Warning! Already in CONNECTING state');
        resolve(false);
        return;
      }

      if (this.isConnected) {
        Utils.DLog('[Chat]', 'Warning! Chat is already connected!');
        resolve(false);
        return;
      }

      this.isConnecting = true;
      this.isLogout = false;

      this.removeAllXMPPClientListeners();

      this.addXMPPClientListener('connect', () => {
        Utils.DLog('[Chat]', this.isReconnect ? 'RECONNECTING' : 'CONNECTING');
      });

      this.addXMPPClientListener('online', () => {
        Utils.DLog('[Chat]', 'ONLINE');
        this.postConnectActions();
        resolve(true);
        this.connectPromise = null;
      });

      this.addXMPPClientListener('offline', () => {
        Utils.DLog('[Chat]', 'OFFLINE');
      });

      this.addXMPPClientListener('disconnect', () => {
        Utils.DLog('[Chat]', 'DISCONNECTED');

        Utils.safeCallbackCall(this.onDisconnectedListener)();

        if (config.chat.reconnect?.enable) {
          this.isConnected = false;
          this.isConnecting = false;
        } else {
          this.disconnect();
        }
      });

      this.addXMPPClientListener('status', (status: Chat.ConnectionStatus, value: unknown) => {
        Utils.DLog('[Chat]', `status - ${String(status)}`, typeof value === 'object' ? JSON.stringify(value) : '');
        Utils.safeCallbackCall(this.onChatStatusListener)(status);
      });

      this.addXMPPClientListener('stanza', (stanza: Chat.XmlElement) => {
        // it can be a case,
        // when message came after xmpp auth but before resource binding,
        // and it can cause some crashes, e.g.
        // https://github.com/ConnectyCube/connectycube-js-sdk-releases/issues/28
        if (stanza.is('message') && !this.isConnected) {
          this.earlyIncomingMessagesQueue.push(stanza);
          Utils.DLog('[Chat]', "on 'stanza': enqueue incoming stanza (isConnected=false)");
          return;
        }

        // after 'input' and 'element' (only if stanza, not nonza)
        if (stanza.is('presence')) {
          this.onPresence(stanza);
        } else if (stanza.is('iq')) {
          this.onIQ(stanza);
        } else if (stanza.is('message')) {
          if (stanza.attrs.type === 'headline') {
            this.onSystemMessage(stanza);
          } else if (stanza.attrs.type === 'error') {
            this.onMessageError(stanza);
          } else {
            this.onMessage(stanza);
          }
        }
      });

      this.addXMPPClientListener('error', (error: unknown) => {
        Utils.DLog('[Chat]', 'ERROR:', error, {
          isReconnect: this.isReconnect,
          connectPromise: !!this.connectPromise,
        });

        if (this.connectPromise) {
          if (!this.isReconnect) {
            const err = error as { name?: string; condition?: unknown };
            reject(err.name === 'SASLError' ? err.condition : error);
            this.connectPromise = null;
          }
        } else {
          Utils.safeCallbackCall(this.onConnectionErrorListener)(error);
        }
      });

      this.addXMPPClientListener('output', (str) => {
        Utils.callTrafficUsageCallback('xmppDataWrite', { body: str });
        Utils.DLog('[Chat]', 'SENT:', str);
      });

      this.addXMPPClientListener('input', (str) => {
        Utils.callTrafficUsageCallback('xmppDataRead', { body: str });
        Utils.DLog('[Chat]', 'RECV:', str);
      });

      // save user connection data so they will be used when authenticate (above)
      this.xmppClient.options.username = ChatUtils.buildUserJidLocalPart(params.userId);
      this.xmppClient.options.password = params.password;
      // start connect
      this.xmppClient.start().catch((error) => {
        console.error('[Chat] xmppClient.start error', error);
        if (this.connectPromise) {
          reject(error);
          this.connectPromise = null;
        } else {
          Utils.safeCallbackCall(this.onConnectionErrorListener)(error);
        }
      });
    });

    return this.connectPromise;
  }

  public async ping(): Promise<void> {
    return new Promise((resolve, reject) => {
      const id = ChatUtils.getUniqueId('ping');
      const iqStanza = ChatUtils.createIqStanza({ id, type: 'get', to: config.endpoints.chat });

      iqStanza.c('ping', { xmlns: 'urn:xmpp:ping' });

      this.stanzasCallbacks[id] = (stanza: Chat.XmlElement) => {
        const error = ChatUtils.getElement(stanza, 'error');
        if (error) {
          reject(ChatUtils.buildErrorFromXMPPErrorStanza(error));
        } else {
          resolve();
        }
      };

      this.xmppClient.sendOnline(iqStanza);
    });
  }

  public send(dialogId: Chat.DialogId | Chat.UserId, message: Chat.MessageParams = {}): string {
    const messageId = message.id ?? Utils.getBsonObjectId();
    const messageStanza = ChatUtils.createMessageStanza({
      from: this.helpers.userCurrentJid,
      to: this.helpers.jidOrUserId(dialogId),
      type: message.type ?? this.helpers.typeChat(dialogId),
      id: messageId,
    });

    if (!message.id) {
      message.id = messageId;
    }

    if (message.body) {
      messageStanza.c('body').t(message.body).up();
    }

    if (message.extension) {
      messageStanza.c('extraParams', { xmlns: 'jabber:client' });
      ChatUtils.assignExtraParamsToXml(messageStanza, message.extension);
    }

    if (this.streamManagement.supported) {
      this.streamManagement.sendAndCount(messageStanza, message);
    } else {
      this.xmppClient.sendOnline(messageStanza);
    }

    return messageId;
  }

  public sendSystemMessage(
    userId: Chat.UserId | Chat.UserId[] | Chat.DialogId,
    message: Chat.SystemMessageParams
  ): string {
    const messageId = message.id ?? Utils.getBsonObjectId();
    const recipients = Array.isArray(userId) ? userId : [userId];

    for (const recipient of recipients) {
      const messageStanza = ChatUtils.createMessageStanza({
        type: 'headline',
        id: messageId,
        to: this.helpers.jidOrUserId(recipient as Chat.UserId),
      });

      if (message.body) {
        messageStanza.c('body').t(message.body).up();
      }

      if (message.extension) {
        messageStanza.c('extraParams', { xmlns: 'jabber:client' }).c('moduleIdentifier').t('SystemNotifications').up();
        ChatUtils.assignExtraParamsToXml(messageStanza, message.extension);
      }

      this.xmppClient.sendOnline(messageStanza);
    }

    return messageId;
  }

  public sendWebRTCSignalingMessage(
    userId: Chat.UserId,
    message: Chat.SystemMessageParams<Calls.ExtensionParams>
  ): string {
    const messageId = message.id ?? Utils.getBsonObjectId();

    let messageStanza = ChatUtils.createMessageStanza({
      to: this.helpers.jidOrUserId(userId),
      type: 'headline',
      id: messageId,
    }).c('extraParams', { xmlns: 'jabber:client' });

    const extension = message.extension ?? {};
    Object.keys(extension).forEach((name) => {
      const item = extension[name];

      if (name === 'iceCandidates' && Utils.isArray(item)) {
        messageStanza = messageStanza.c('iceCandidates');
        ((item as RTCIceCandidateInit[]) || []).forEach((candidate) => {
          messageStanza = messageStanza.c('iceCandidate');
          if (Utils.isObject(candidate)) {
            Object.entries(candidate).forEach(([key, value]) => {
              messageStanza = messageStanza
                .c(key)
                .t(String(value || ''))
                .up();
            });
          }
          messageStanza = messageStanza.up();
        });
        messageStanza = messageStanza.up();
      } else if (name === 'opponentsIDs' && Utils.isArray(item)) {
        messageStanza = messageStanza.c('opponentsIDs');
        ((item as number[] | string[]) || []).forEach((opponentId) => {
          messageStanza = messageStanza
            .c('opponentID')
            .t(String(opponentId ?? ''))
            .up();
        });
        messageStanza = messageStanza.up();
      } else if (typeof item === 'object' && item !== null) {
        ChatUtils.assignObjectToXml(messageStanza, item as PlainObject, name);
      } else {
        messageStanza = messageStanza
          .c(name)
          .t(String(item ?? ''))
          .up();
      }
    });

    messageStanza = messageStanza.up();

    this.xmppClient.sendOnline(messageStanza);

    return messageId;
  }

  public sendIsTypingStatus(dialogId: Chat.DialogId | Chat.UserId): void {
    const messageStanza = ChatUtils.createMessageStanza({
      from: this.helpers.userCurrentJid,
      to: this.helpers.jidOrUserId(dialogId),
      type: this.helpers.typeChat(dialogId),
    });

    messageStanza.c('composing', { xmlns: 'http://jabber.org/protocol/chatstates' });

    this.xmppClient.sendOnline(messageStanza);
  }

  public sendIsStopTypingStatus(dialogId: Chat.DialogId | Chat.UserId): void {
    const messageStanza = ChatUtils.createMessageStanza({
      from: this.helpers.userCurrentJid,
      to: this.helpers.jidOrUserId(dialogId),
      type: this.helpers.typeChat(dialogId),
    });

    messageStanza.c('paused', { xmlns: 'http://jabber.org/protocol/chatstates' });

    this.xmppClient.sendOnline(messageStanza);
  }

  public sendDeliveredStatus(params: Chat.MessageStatusParams): void {
    const messageStanza = ChatUtils.createMessageStanza({
      type: 'chat',
      from: this.helpers.userCurrentJid,
      id: Utils.getBsonObjectId(),
      to: this.helpers.jidOrUserId(params.userId!),
    });

    messageStanza.c('received', { xmlns: 'urn:xmpp:chat-markers:0', id: params.messageId }).up();
    messageStanza.c('extraParams', { xmlns: 'jabber:client' }).c('dialog_id').t(params.dialogId);

    this.xmppClient.sendOnline(messageStanza);
  }

  public sendReadStatus(params: Chat.MessageStatusParams): void {
    const messageStanza = ChatUtils.createMessageStanza({
      type: 'chat',
      from: this.helpers.userCurrentJid,
      to: this.helpers.jidOrUserId(params.userId!),
      id: Utils.getBsonObjectId(),
    });

    messageStanza.c('displayed', { xmlns: 'urn:xmpp:chat-markers:0', id: params.messageId }).up();
    messageStanza.c('extraParams', { xmlns: 'jabber:client' }).c('dialog_id').t(params.dialogId);

    this.xmppClient.sendOnline(messageStanza);
  }

  public editMessage(params: Chat.EditMessageParams): void {
    const messageStanza = ChatUtils.createMessageStanza({
      from: this.helpers.userCurrentJid,
      to: this.helpers.jidOrUserId(params.to ?? params.dialogId),
      type: this.helpers.typeChat(params.to ?? params.dialogId),
      id: Utils.getBsonObjectId(),
    });

    messageStanza.c('body').t(params.body).up();
    messageStanza
      .c('replace', {
        xmlns: 'urn:xmpp:message-correct:0',
        id: params.originMessageId,
        last: params.last ? 'true' : 'false',
      })
      .up();
    messageStanza.c('extraParams', { xmlns: 'jabber:client' }).c('dialog_id').t(params.dialogId);

    if (params.extension) {
      ChatUtils.assignExtraParamsToXml(messageStanza, params.extension!);
    }

    this.xmppClient.sendOnline(messageStanza);
  }

  public deleteMessage(params: Chat.DeleteMessageParams): void {
    const messageStanza = ChatUtils.createMessageStanza({
      from: this.helpers.userCurrentJid,
      to: this.helpers.jidOrUserId(params.to ?? params.dialogId),
      type: this.helpers.typeChat(params.to ?? params.dialogId),
      id: Utils.getBsonObjectId(),
    });

    messageStanza.c('remove', { xmlns: 'urn:xmpp:message-delete:0', id: params.messageId }).up();
    messageStanza.c('extraParams', { xmlns: 'jabber:client' }).c('dialog_id').t(params.dialogId);

    this.xmppClient.sendOnline(messageStanza);
  }

  public getLastUserActivity(userId: Chat.UserId): Promise<Chat.LastActivity> {
    return new Promise((resolve, reject) => {
      const id = ChatUtils.getUniqueId('lastActivity');
      const iqStanza = ChatUtils.createIqStanza({
        type: 'get',
        from: this.helpers.userCurrentJid,
        to: this.helpers.jidOrUserId(userId),
        id: id,
      });

      iqStanza.c('query', { xmlns: 'jabber:iq:last' });

      this.stanzasCallbacks[id] = (stanza: Chat.XmlElement) => {
        if (ChatUtils.getElement(stanza, 'error')) {
          reject(ChatUtils.buildErrorFromXMPPErrorStanza(stanza));
        } else {
          resolve(this.onLastActivityStanza(stanza));
        }
      };

      this.xmppClient.sendOnline(iqStanza);
    });
  }

  private onLastActivityStanza(stanza: Chat.XmlElement): Chat.LastActivity {
    const from = ChatUtils.getAttr(stanza, 'from');
    const userId = this.helpers.getUserIdFromJID(from);
    const query = ChatUtils.getElement(stanza, 'query');
    const seconds = query ? parseInt(ChatUtils.getAttr(query, 'seconds')) : 0;

    Utils.safeCallbackCall(this.onLastUserActivityListener)(userId, seconds);

    return { userId, seconds };
  }

  public markActive(): void {
    const iqStanza = ChatUtils.createIqStanza({
      id: this.helpers.getUniqueId('markActive'),
      type: 'set',
    });

    iqStanza.c('mobile', {
      xmlns: 'http://tigase.org/protocol/mobile#v2',
      enable: 'false',
    });

    this.xmppClient.sendOnline(iqStanza);
  }

  public markInactive(): void {
    const iqStanza = ChatUtils.createIqStanza({
      id: this.helpers.getUniqueId('markActive'),
      type: 'set',
    });

    iqStanza.c('mobile', {
      xmlns: 'http://tigase.org/protocol/mobile#v2',
      enable: 'true',
    });

    this.xmppClient.sendOnline(iqStanza);
  }

  public async disconnect(): Promise<boolean> {
    Utils.DLog('[Chat]', 'disconnect');

    if (this.isLogout) {
      Utils.DLog('[Chat]', 'Warning! Chat is already disconnected!');
      return true;
    }

    this.isLogout = true;
    this.isReconnect = false;
    this.isConnected = false;
    this.isConnecting = false;
    this.muc.joinedRooms = {};
    this.helpers.userCurrentJid = '';
    this.streamManagement.stop();

    try {
      await this.xmppClient.stop();
      return true;
    } catch {
      return false;
    }
  }

  public terminate(): void {
    Utils.DLog('[Chat]', 'terminated');

    this.isConnected = false;
    this.isConnecting = false;
    this.streamManagement.stop();
    this.xmppClient.socket?.end();
  }

  private onMessage(rawStanza: Chat.XmlElement): void {
    const forwardedStanza = ChatUtils.getElementTreePath(rawStanza, ['sent', 'forwarded', 'message']);
    const stanza = forwardedStanza || rawStanza;
    const from = ChatUtils.getAttr(stanza, 'from');
    const type = ChatUtils.getAttr(stanza, 'type');
    const invite = ChatUtils.getElement(stanza, 'invite');
    const resource = this.xmppClient.jid?.getResource() || '';
    const isChat = type === 'chat';

    // ignore private message from the same resource or invite messages from MUC
    if ((isChat && from.includes(resource)) || invite) {
      return;
    }

    const messageId = ChatUtils.getAttr(stanza, 'id');
    const delivered = ChatUtils.getElement(stanza, 'received');
    const read = ChatUtils.getElement(stanza, 'displayed');
    const replaceSubElement = ChatUtils.getElement(stanza, 'replace');
    const reactionsSubElement = ChatUtils.getElement(stanza, 'reactions');
    const removeSubElement = ChatUtils.getElement(stanza, 'remove');
    const composing = Boolean(ChatUtils.getElement(stanza, 'composing'));
    const paused = ChatUtils.getElement(stanza, 'paused');
    const delay = ChatUtils.getElement(stanza, 'delay');
    const extraParams = ChatUtils.getElement(stanza, 'extraParams');
    const body = ChatUtils.getElementText(stanza, 'body');
    const isForwarded = Boolean(forwardedStanza);
    const isGroup = type === 'groupchat';
    const recipient = isChat ? ChatUtils.getAttr(stanza, 'to') : null;
    const recipientId = recipient ? this.helpers.getUserIdFromJID(recipient) : null;
    const ext = extraParams ? ChatUtils.parseExtraParams(extraParams) : null;
    const extension = ext ? ext.extension : null;
    const dialogId = isGroup ? this.helpers.getDialogIdFromJID(from) : (ext?.dialogId ?? null);
    const userId = isGroup ? this.helpers.getIdFromResource(from) : this.helpers.getUserIdFromJID(from);
    const currentUserJid = this.xmppClient.jid?.toString();
    const currentUserId = currentUserJid ? this.helpers.getUserIdFromJID(currentUserJid) : 0;

    // typing statuses
    if (composing || paused) {
      if (isChat || isGroup || !delay) {
        Utils.safeCallbackCall(this.onMessageTypingListener)(composing, userId, dialogId);
      }

      return;
    }

    // edit message
    if (replaceSubElement) {
      Utils.safeCallbackCall(this.onMessageUpdateListener)(
        ChatUtils.getAttr(replaceSubElement, 'id'),
        ChatUtils.getAttr(replaceSubElement, 'last') === 'true',
        body,
        dialogId,
        userId,
        extension
      );

      return;
    }

    // reactions
    if (reactionsSubElement) {
      if (isForwarded && isGroup) {
        return;
      }

      const messageId = ChatUtils.getAttr(reactionsSubElement, 'message_id');
      const userId = parseInt(ChatUtils.getAttr(reactionsSubElement, 'user_id'));
      const { add, remove } = ChatUtils.parseReactions(reactionsSubElement);

      Utils.safeCallbackCall(this.onMessageReactionsListener)(messageId, userId, dialogId, add, remove);

      return;
    }

    // delete message
    if (removeSubElement) {
      const messageId = ChatUtils.getAttr(removeSubElement, 'id');

      Utils.safeCallbackCall(this.onMessageDeleteListener)(messageId, dialogId, userId);

      return;
    }

    // delivered / read statuses
    if (delivered || read) {
      if (delivered && isChat) {
        const id = ChatUtils.getAttr(delivered, 'id');
        Utils.safeCallbackCall(this.onDeliveredStatusListener)(id, dialogId, userId);
      }
      if (read && isChat) {
        const id = ChatUtils.getAttr(read, 'id');
        Utils.safeCallbackCall(this.onReadStatusListener)(id, dialogId, userId);
      }

      return;
    }

    // auto-send 'received' status (ignore messages from yourself)
    if (dialogId && userId && userId !== currentUserId) {
      this.sendDeliveredStatus({ messageId, dialogId, userId });
    }

    const message: Chat.Message = {
      id: messageId,
      dialog_id: dialogId,
      recipient_id: recipientId,
      is_forwarded: isForwarded,
      type,
      body,
      extension,
      delay,
    };

    if (isChat || isGroup) {
      Utils.safeCallbackCall(this.onMessageListener)(userId, message);
    }
  }

  private onPresence(stanza: Chat.XmlElement): void {
    const from = ChatUtils.getAttr(stanza, 'from');
    const id = ChatUtils.getAttr(stanza, 'id');
    const type = ChatUtils.getAttr(stanza, 'type');
    const currentUserJid = this.xmppClient.jid?.toString();
    const currentUserId = this.helpers.getUserIdFromJID(currentUserJid);
    const x = ChatUtils.getElement(stanza, 'x');
    const xmlns = x ? ChatUtils.getAttr(x, 'xmlns') : null;
    const status = x ? ChatUtils.getElement(x, 'xmlns') : null;
    // const statusCode = status ? ChatUtils.getElement(status, 'code') : null;
    const statusCode = status ? ChatUtils.getElementText(status, 'code') : null;

    // MUC presences
    if (xmlns && xmlns.startsWith('http://jabber.org/protocol/muc')) {
      // Error
      if (type === 'error') {
        // JOIN to dialog error
        if (id.endsWith(':join')) {
          Utils.safeCallbackCall(this.stanzasCallbacks[id])(stanza);
        }
        return;
      }

      const dialogId = this.helpers.getDialogIdFromJID(from);
      const mucUserId = this.helpers.getUserIdFromRoomJid(from);

      // self presence
      if (status) {
        // KICK from dialog event
        if (statusCode === '301') {
          const item = ChatUtils.getElement(stanza, 'item');
          const actorElement = item ? ChatUtils.getElement(item, 'actor') : null;
          const initiatorUserJid = actorElement ? ChatUtils.getAttr(actorElement, 'jid') : null;
          const initiatorId = this.helpers.getUserIdFromJID(initiatorUserJid ?? undefined);
          const roomJid = this.helpers.getRoomJidFromRoomFullJid(from);

          Utils.safeCallbackCall(this.onKickOccupant)(dialogId, initiatorId);

          if (roomJid) {
            delete this.muc.joinedRooms[roomJid];
          }

          return;
        } else {
          if (type === 'unavailable') {
            // LEAVE response
            if (status && statusCode === '110') {
              Utils.safeCallbackCall(this.stanzasCallbacks['muc:leave'])(null);
            }
            return;
          }
          // JOIN response
          if (id.endsWith(':join') && status && statusCode === '110') {
            this.stanzasCallbacks[id]?.(stanza);
            return;
          }
        }
        // Occupants JOIN/LEAVE events
      } else if (mucUserId != currentUserId) {
        const listenerName = type === 'unavailable' ? 'onLeaveOccupant' : 'onJoinOccupant';

        Utils.safeCallbackCall(this[listenerName])(dialogId, mucUserId);

        return;
      }
    }
  }

  private onIQ(stanza: Chat.XmlElement): void {
    const stanzaId = ChatUtils.getAttr(stanza, 'id');

    if (this.stanzasCallbacks[stanzaId]) {
      Utils.safeCallbackCall(this.stanzasCallbacks[stanzaId])(stanza);
      delete this.stanzasCallbacks[stanzaId];
    } else {
      const from = ChatUtils.getAttr(stanza, 'from');
      const query = ChatUtils.getElement(stanza, 'query');
      if (from && query) {
        this.onLastActivityStanza(stanza);
      }
    }
  }

  private onSystemMessage(rawStanza: Chat.XmlElement): void {
    const forwardedStanza = ChatUtils.getElementTreePath(rawStanza, ['sent', 'forwarded', 'message']);
    const stanza = forwardedStanza || rawStanza;
    const from = ChatUtils.getAttr(stanza, 'from');
    const messageId = ChatUtils.getAttr(stanza, 'id');
    const extraParams = ChatUtils.getElement(stanza, 'extraParams');
    const userId = this.helpers.getUserIdFromJID(from);
    const delay = ChatUtils.getElement(stanza, 'delay');
    const moduleIdentifier = extraParams ? ChatUtils.getElementText(extraParams, 'moduleIdentifier') : null;
    const body = ChatUtils.getElementText(stanza, 'body');
    const ext = extraParams ? ChatUtils.parseExtraParams(extraParams) : null;
    const extension = ext ? ext.extension : null;

    switch (moduleIdentifier) {
      case 'SystemNotifications':
        Utils.safeCallbackCall(this.onSystemMessageListener)({ id: messageId, userId, body, extension });
        break;
      case CallSignalingModuleIdentifier:
        if (!delay && userId && extraParams) {
          this.onWebRTCSignalingMessage(userId, extraParams);
        }

        if (this.webrtcSignalingProcessor && !delay) {
          Utils.safeCallbackCall(this.webrtcSignalingProcessor.onMessage)(userId, extraParams);
        }
        break;
      default:
        break;
    }
  }

  private onWebRTCSignalingMessage(userId: Chat.UserId, extensionElem: Chat.XmlElement): void {
    if (!this.webrtcSignalingProcessor) {
      return;
    }

    const extraParams = this.getCallExtension(extensionElem);

    Utils.safeCallbackCall(this.webrtcSignalingProcessor.onMessage)(userId, extraParams);
  }

  private getCallExtension(extraParams?: Chat.XmlElement): Calls.ExtensionParams {
    if (!extraParams) {
      return {};
    }

    const extension: Calls.ExtensionParams = { iceCandidates: [], opponentsIDs: [] };
    const elements = ChatUtils.getChildElements(extraParams);

    elements.forEach((element: Chat.XmlElement) => {
      const items = ChatUtils.getChildElements(element);
      const key = ChatUtils.getName(element) ?? '';

      switch (key) {
        case 'callerID':
          extension[key] = parseInt(ChatUtils.getText(element) || '0');
          break;

        case 'callType':
          const type = ChatUtils.getText(element);
          extension[key] = type === '1' ? CallType.VIDEO : type === '2' ? CallType.AUDIO : undefined;
          break;

        case 'iceCandidates':
          items.forEach((item) => {
            const candidate: StringObject = {};
            ChatUtils.getChildElements(item).forEach((child) => {
              const childName = ChatUtils.getName(child) || '';
              const childText = ChatUtils.getText(child);
              candidate[childName] =
                childName === 'sdpMLineIndex' ? String(parseInt(childText || '0', 10)) : (childText ?? '');
            });
            extension.iceCandidates?.push(candidate);
          });
          break;

        case 'opponentsIDs':
          items.forEach((item) => {
            extension.opponentsIDs?.push(parseInt(ChatUtils.getText(item) || '0'));
          });
          break;

        default:
          if (items.length > 1) {
            if ((ChatUtils.getText(element) || '').length > 4096) {
              extension[key] = items.reduce((acc, item) => acc + ChatUtils.getText(item), '');
            } else {
              ChatUtils.assignXmlToObject(element, extension);
            }
          } else {
            if (key === 'userInfo') {
              ChatUtils.assignXmlToObject(element, extension);
            } else {
              extension[key] = ChatUtils.getText(element);
            }
          }
          break;
      }
    });

    if (extension.iceCandidates?.length === 0) {
      delete extension.iceCandidates;
    }

    if (extension.opponentsIDs?.length === 0) {
      delete extension.opponentsIDs;
    }

    return extension;
  }

  private onMessageError(stanza: Chat.XmlElement): void {
    // <error code="503" type="cancel">
    //   <service-unavailable xmlns="urn:ietf:params:xml:ns:xmpp-stanzas"/>
    //   <text xmlns="urn:ietf:params:xml:ns:xmpp-stanzas" xml:lang="en">Service not available.</text>
    // </error>
    const messageId = ChatUtils.getAttr(stanza, 'id');
    const error = ChatUtils.buildErrorFromXMPPErrorStanza(stanza);

    Utils.safeCallbackCall(this.onMessageErrorListener)(messageId, error);
  }

  private postConnectActions(): void {
    Utils.DLog('[Chat]', this.isReconnect ? 'RECONNECTED' : 'CONNECTED');

    const presence = ChatUtils.createPresenceStanza();

    this.streamManagement.start();
    this.helpers.userCurrentJid = this.xmppClient.jid?.toString() ?? null;
    this.isConnected = true;
    this.isConnecting = false;
    this.enableCarbons();
    this.xmppClient.sendOnline(presence); // initial presence

    if (this.isReconnect) {
      // reconnect
      Utils.safeCallbackCall(this.onReconnectListener)();
    } else {
      this.isReconnect = true;
    }

    if (this.earlyIncomingMessagesQueue.length > 0) {
      Utils.DLog('[Chat]', `Flush 'earlyIncomingMessagesQueue' (length=${this.earlyIncomingMessagesQueue.length})`);

      const stanzasCallback = this.xmppClientListeners.get('stanza');

      this.earlyIncomingMessagesQueue.forEach((stanza) => {
        stanzasCallback?.(stanza);
      });
      this.earlyIncomingMessagesQueue = [];
    }
  }

  private enableCarbons(): void {
    const iqStanza = ChatUtils.createIqStanza({
      type: 'set',
      from: this.helpers.userCurrentJid,
      id: ChatUtils.getUniqueId('enableCarbons'),
    });

    iqStanza.c('enable', { xmlns: 'urn:xmpp:carbons:2' });

    this.xmppClient.sendOnline(iqStanza);
  }

  private setSubscriptionToUserLastActivity(userId: Chat.UserId, enable: boolean): void {
    const iqStanza = ChatUtils.createIqStanza({
      id: this.helpers.getUniqueId('statusStreaming'),
      type: 'set',
    });

    iqStanza.c('subscribe', {
      xmlns: 'https://connectycube.com/protocol/status_streaming',
      user_jid: this.helpers.jidOrUserId(userId),
      enable,
    });

    this.xmppClient.sendOnline(iqStanza);
  }

  subscribeToUserLastActivityStatus(userId: Chat.UserId) {
    this.setSubscriptionToUserLastActivity(userId, true);
  }

  unsubscribeFromUserLastActivityStatus(userId?: Chat.UserId) {
    if (userId !== undefined) this.setSubscriptionToUserLastActivity(userId, false);
  }

  protected override getListenerByName(name: ChatEvent): Chat.ListenerName | null {
    switch (name) {
      case ChatEvent.ERROR_MESSAGE:
        return 'onMessageErrorListener';
      case ChatEvent.DELIVERED_MESSAGE:
        return 'onDeliveredStatusListener';
      case ChatEvent.SENT_MESSAGE:
        return 'onSentMessageCallback';
      case ChatEvent.JOIN:
        return 'onJoinOccupant';
      case ChatEvent.LEAVE:
        return 'onLeaveOccupant';
      case ChatEvent.KICK:
        return 'onKickOccupant';
      default:
        return super.getListenerByName(name);
    }
  }

  private addXMPPClientListener(name: Chat.XMPPClientEvent, listener: Chat.XMPPClientListener): void {
    switch (name) {
      case 'output':
        this.xmppClient.middleware.filter(async (context, next) => {
          listener(context.stanza?.toString());
          await next();
        });
        break;

      case 'input':
        const _listener = (stanza: Chat.XmlElement) => {
          listener(stanza.toString());
        };
        this.xmppClient.on('element', _listener);
        this.xmppClientListeners.set(name, _listener);
        break;

      default:
        this.xmppClient.on(name, listener);
        this.xmppClientListeners.set(name, listener);
        break;
    }
  }

  private removeXMPPClientListener(name: Chat.XMPPClientEvent): void {
    const listener = this.xmppClientListeners.get(name);
    if (listener) {
      this.xmppClient.removeListener(name, listener);
    }
    this.xmppClientListeners.delete(name);
  }

  private removeAllXMPPClientListeners(): void {
    this.xmppClientListeners.forEach((_, name) => this.removeXMPPClientListener(name));
  }
}
